<!-- TimelinePanel.vue - LINHA CINZA + TRIÂNGULO #ff905f -->
<template>
  <div class="h-full overflow-y-auto custom-scrollbar" ref="scrollContainer">
    <div class="p-6 space-y-6">

      <!-- Panel Header -->
      <div class="pb-4 border-b border-dark">
        <h1 class="text-lg font-bold text-dark-primary mb-1">Timeline</h1>
        <p class="text-xs text-dark-tertiary">Chronological event log</p>
      </div>

      <!-- Filters -->
<div class="flex items-center gap-2">
        <button
          v-for="filter in filters"
          :key="filter.value"
          @click="activeFilter = filter.value"
          class="px-3 py-1.5 rounded text-xs font-medium transition"
          :class="activeFilter === filter.value 
            ? 'bg-accent/10 border border-accent text-accent' 
            : 'bg-dark-tertiary border border-transparent text-dark-secondary hover:bg-dark-quaternary'"
        >
          {{ filter.label }}
        </button>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin"></div>
      </div>

      <!-- Empty state -->
      <div v-else-if="filteredEvents.length === 0" class="flex flex-col items-center justify-center py-12 text-center">
        <Clock class="w-12 h-12 text-dark-secondary opacity-40 mb-4" />
        <p class="text-sm text-dark-secondary mb-1">No events found</p>
        <p class="text-xs text-dark-tertiary">Try adjusting your filters</p>
      </div>

      <!-- Timeline -->
      <div v-else class="relative pl-8">
<div 
          class="absolute left-[2px] top-2 bottom-2 w-[2px] rounded-full"
          style="background-color: var(--border-color);"
        ></div>

        <!-- Current position indicator (sliding triangle) - COR #ff905f -->
        <div
          class="absolute left-[-6px] transition-all duration-300 z-30"
          :style="{ top: currentPositionY + 'px' }"
        >
          <Play class="w-6 h-6 fill-current" style="color: #ff905f;" />
        </div>

        <!-- Events -->
        <div class="space-y-4">
          <div
            v-for="(event, idx) in filteredEvents"
            :key="idx"
            class="relative"
            :ref="el => eventRefs[idx] = el"
          >
            <!-- Event card -->
            <div class="ml-4">
              <!-- Session start/end -->
              <div v-if="event.type === 'start' || event.type === 'end'" class="py-1">
                <div class="flex items-center gap-2">
                  <div class="w-2 h-2 rounded-full bg-dark-secondary"></div>
                  <component :is="event.type === 'start' ? Play : Square" class="w-3 h-3 text-dark-secondary" />
                  <span class="text-xs text-dark-secondary font-mono">{{ formatTime(event.timestamp) }}</span>
                  <span class="text-sm font-medium text-dark-primary">{{ event.label }}</span>
                </div>
              </div>

              <!-- Command -->
              <div
                v-else-if="event.type === 'command'"
                @click="seekToEvent(event)"
                class="bg-dark-tertiary border border-dark rounded-lg p-3 cursor-pointer hover:border-accent/50 hover:bg-dark-quaternary transition"
              >
                <div class="flex items-start gap-2">
                  <Terminal class="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <div class="flex-1 min-w-0">
                    <span class="text-xs text-dark-secondary font-mono block mb-1">{{ formatTime(event.timestamp) }}</span>
                    <p class="text-sm text-dark-primary font-mono break-all">{{ event.command }}</p>
                  </div>
                </div>
              </div>

              <!-- Detection -->
              <div v-else-if="event.type === 'detection'">
                <div
                  @click="toggleDetection(event.id)"
                  class="bg-dark-primary border border-error/30 rounded-lg p-3 cursor-pointer hover:border-error/50 hover:bg-dark-tertiary transition"
                >
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex items-start gap-2 flex-1 min-w-0">
                      <Shield class="w-4 h-4 text-error flex-shrink-0 mt-0.5" />
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2 mb-1">
                          <span class="text-xs text-dark-secondary font-mono">{{ formatTime(event.timestamp) }}</span>
                          <span class="text-xs font-mono text-error">{{ event.detection.technique_id }}</span>
                        </div>
                        <p class="text-sm font-semibold text-dark-primary">{{ event.detection.tactic_name }}</p>
                        <p class="text-xs text-dark-secondary">{{ event.detection.technique_name }}</p>
                      </div>
                    </div>
                    <ChevronRight 
                      class="w-4 h-4 text-dark-secondary transition-transform duration-200 flex-shrink-0"
                      :class="{ 'rotate-90': expandedDetections[event.id] }"
                    />
                  </div>

                  <!-- Expanded details -->
                  <div v-if="expandedDetections[event.id]" class="mt-4 pt-4 border-t border-dark space-y-3">
                    <!-- Confidence -->
                    <div>
                      <div class="flex items-center justify-between mb-2">
                        <span class="text-xs text-dark-secondary uppercase tracking-wide">Confidence</span>
                        <span class="text-sm font-bold text-dark-primary">{{ Math.round(event.detection.confidence * 100) }}%</span>
                      </div>
                      <div class="h-2 bg-dark-tertiary rounded-full overflow-hidden">
                        <div 
                          class="h-full transition-all duration-500"
                          :style="{ 
                            width: `${event.detection.confidence * 100}%`,
                            background: confidenceColor(event.detection.confidence)
                          }"
                        ></div>
                      </div>
                    </div>

                    <!-- Summary -->
                    <div v-if="event.detection.summary">
                      <p class="text-xs text-dark-secondary uppercase tracking-wide mb-2">Summary</p>
                      <p class="text-sm text-dark-primary leading-relaxed">{{ event.detection.summary }}</p>
                    </div>

                    <!-- View in Analysis button -->
                    <button
                      @click.stop="viewInAnalysis"
                      class="w-full px-3 py-2 bg-dark-tertiary border border-accent/30 rounded text-xs font-medium text-accent hover:bg-accent/10 transition"
                    >
                      View in Analysis
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { Clock, Terminal, Shield, Play, Square, ChevronRight } from "lucide-vue-next";
import { usePlayerStore } from "../stores/playerStore";
import { useAuthStore }   from "../stores/authStore";
import { apiClient }      from "../services/apiClient";

const props = defineProps({
  sessionId: {
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["seek-frame", "switch-tab"]);

const player    = usePlayerStore();
const authStore = useAuthStore();
const scrollContainer = ref(null);
const eventRefs = ref([]);

const loading = ref(true);
const detections = ref([]);
const commands = ref([]);
const expandedDetections = ref({});
const activeFilter = ref("all");
const currentPositionY = ref(0);

const filters = [
  { value: "all", label: "All" },
  { value: "ttps", label: "TTPs" },
  { value: "commands", label: "Commands" },
];

const allEvents = computed(() => {
  const events = [];

  // Session start
  events.push({
    type: "start",
    timestamp: 0,
    label: "Session Start",
  });

  // Detections
  detections.value.forEach((detection, idx) => {
    const firstFrame = detection.evidence_frame_ids?.[0] || 0;
    events.push({
      type: "detection",
      timestamp: firstFrame,
      id: `det-${idx}`,
      detection,
      label: detection.tactic_name,
    });
  });

  // Commands
  commands.value.forEach((cmd, idx) => {
    events.push({
      type: "command",
      timestamp: cmd.timestamp_ms || 0,
      id: `cmd-${idx}`,
      command: cmd.data?.command || "Unknown command",
    });
  });

  // Session end
  if (props.duration > 0) {
    events.push({
      type: "end",
      timestamp: props.duration,
      label: "Session End",
    });
  }

  // Sort by timestamp
  return events.sort((a, b) => a.timestamp - b.timestamp);
});

const filteredEvents = computed(() => {
  if (activeFilter.value === "all") return allEvents.value;
  if (activeFilter.value === "ttps") {
    return allEvents.value.filter(e => e.type === "detection" || e.type === "start" || e.type === "end");
  }
  if (activeFilter.value === "commands") {
    return allEvents.value.filter(e => e.type === "command" || e.type === "start" || e.type === "end");
  }
  return allEvents.value;
});

onMounted(async () => {
  await Promise.all([loadDetections(), loadCommands()]);
  updateCurrentPosition();
});

// Update current position indicator
watch(() => player.currentTime, () => {
  updateCurrentPosition();
});

function updateCurrentPosition() {
  const currentMs = player.currentTime * 1000;
  
  // Find which events the current time falls between
  for (let i = 0; i < filteredEvents.value.length - 1; i++) {
    const current = filteredEvents.value[i];
    const next = filteredEvents.value[i + 1];
    
    if (currentMs >= current.timestamp && currentMs < next.timestamp) {
      const currentEl = eventRefs.value[i];
      const nextEl = eventRefs.value[i + 1];
      
      if (currentEl && nextEl) {
        const currentTop = currentEl.offsetTop;
        const nextTop = nextEl.offsetTop;
        const progress = (currentMs - current.timestamp) / (next.timestamp - current.timestamp);
        currentPositionY.value = currentTop + (nextTop - currentTop) * progress;
        return;
      }
    }
  }
  
  // Default to first event
  if (eventRefs.value[0]) {
    currentPositionY.value = eventRefs.value[0].offsetTop;
  }
}

async function loadDetections() {
  try {
    const result = await apiClient(`/detections?session_id=${props.sessionId}`);
    detections.value = result?.data || [];
  } catch (error) {
    console.error("Failed to load detections:", error);
    detections.value = [];
  }
}

async function loadCommands() {
  try {
    const result = await apiClient(`/frames/input_events?session_id=${props.sessionId}&type=command`);
    console.log("✅ Commands loaded:", result?.data?.length || 0);
    commands.value = result?.data || [];
  } catch (error) {
    console.error("❌ Failed to load commands:", error);
    commands.value = [];
  } finally {
    loading.value = false;
  }
}

function toggleDetection(id) {
  expandedDetections.value[id] = !expandedDetections.value[id];
}

function seekToEvent(event) {
  emit("seek-frame", event.timestamp);
}

function viewInAnalysis() {
  emit("switch-tab", "analysis");
}

function formatTime(ms) {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function confidenceColor(confidence) {
  if (confidence >= 0.8) return "var(--error)";
  if (confidence >= 0.6) return "var(--warning)";
  return "var(--accent)";
}
</script>

<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}
.custom-scrollbar::-webkit-scrollbar { width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.1); border-radius: 3px; }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>